import { useEffect, useRef, useState, type ReactElement } from 'react';
import { useEditor } from '../store';
import { brauchbar, hochladen, ziehtDateien } from '../hochladen';
import { Icon } from './Icon';

/**
 * **Dateien mit der Maus hereinziehen** (Sascha, 15. Oktober: „Drag&drop als
 * Datei muss gehen! Wenn ich was in den Browser ziehe -- Drop Felder:
 * Timeline Position. Oder in die Medien.“).
 *
 * Sobald eine Datei über dem Fenster schwebt, erscheinen die beiden Ziele:
 * oben das Feld „In die Medien“, unten die Zeitleiste selbst, auf der eine
 * Linie zeigt, wohin es kommt (das macht die Zeitleiste, `Timeline.tsx`).
 * Fällt eine Datei daneben, passiert nichts -- vor allem öffnet der Browser
 * sie nicht an Stelle des Schnitts, was er ohne dieses Stück täte.
 *
 * Und solange etwas hochlädt, steht unten, was und wie weit: die Datei kann
 * ja ohne offenes Blatt gekommen sein.
 */
export function Ablegen({ fortschritt }: { fortschritt: boolean }): ReactElement | null {
  const role = useEditor((s) => s.role);
  const toast = useEditor((s) => s.toast);
  const laeuft = useEditor((s) => s.hochladen);
  const [aktiv, setAktiv] = useState(false);
  const [ueber, setUeber] = useState(false);
  const aus = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const ziehen = (e: DragEvent) => {
      if (!ziehtDateien(e)) return;
      const zeitleiste = e.defaultPrevented; // die Zeitleiste nimmt es an
      const medien = Boolean((e.target as Element | null)?.closest?.('[data-ablage="medien"]'));
      // Ohne das öffnet der Browser die Datei und der Schnitt ist weg. Neben
      // den Zielen zeigt der Zeiger „geht nicht“, und losgelassen passiert
      // dort nichts.
      e.preventDefault();
      if (e.dataTransfer) e.dataTransfer.dropEffect = role !== 'viewer' && (zeitleiste || medien) ? 'copy' : 'none';
      setAktiv(true);
      setUeber(medien);
      // Verlässt die Datei das Fenster, kommt kein `dragover` mehr -- und ein
      // `dragleave` kommt bei jedem Kind-Element, auch mitten im Fenster.
      if (aus.current) clearTimeout(aus.current);
      aus.current = setTimeout(() => {
        setAktiv(false);
        setUeber(false);
      }, 300);
    };
    const fallen = (e: DragEvent) => {
      if (!ziehtDateien(e)) return;
      const erledigt = e.defaultPrevented; // die Zeitleiste hat es schon genommen
      e.preventDefault();
      if (aus.current) clearTimeout(aus.current);
      setAktiv(false);
      setUeber(false);
      if (erledigt) return;
      if (role === 'viewer') {
        toast('info', 'Mit „nur ansehen“ lässt sich nichts hinzufügen.');
        return;
      }
      if (!(e.target as Element | null)?.closest?.('[data-ablage="medien"]')) return;
      const { gut, weg } = brauchbar(e.dataTransfer?.files);
      if (weg.length) toast('error', `${weg.map((n) => `„${n}“`).join(', ')}: kein Video, Ton oder Bild.`);
      if (gut.length) void hochladen(gut, null);
    };
    window.addEventListener('dragover', ziehen);
    window.addEventListener('drop', fallen);
    return () => {
      window.removeEventListener('dragover', ziehen);
      window.removeEventListener('drop', fallen);
      if (aus.current) clearTimeout(aus.current);
    };
  }, [role, toast]);

  // Die Zeitleiste zeigt sich als Ziel, solange gezogen wird.
  useEffect(() => {
    document.body.classList.toggle('ablage-laeuft', aktiv && role !== 'viewer');
    return () => document.body.classList.remove('ablage-laeuft');
  }, [aktiv, role]);

  return (
    <>
      {aktiv && role !== 'viewer' ? (
        <div className="ablage-schleier" data-ablage-schleier>
          <div className={`ablage-feld${ueber ? ' ueber' : ''}`} data-ablage="medien">
            <Icon name="plus" size={22} />
            <b>In die Medien</b>
            <span>Hier loslassen: hochladen, noch nicht in den Film.</span>
          </div>
          <div className="ablage-hinweis">Oder auf die Zeitleiste: an die Stelle unter der Maus.</div>
        </div>
      ) : null}
      {fortschritt && laeuft ? (
        <div className="hochladen-pille" data-hochladen-pille role="status">
          <span>
            {laeuft.total > 1 ? `${laeuft.done + 1}/${laeuft.total} · ` : ''}
            {laeuft.name}
          </span>
          <b>{Math.round(laeuft.fraction * 100)}%</b>
          <div className="progressbar">
            <div style={{ width: `${Math.round(laeuft.fraction * 100)}%` }} />
          </div>
        </div>
      ) : null}
    </>
  );
}
